import { useMemo, useCallback } from 'react';

import { LiveTrainingCardProps } from 'src/components/LiveTrainingCard';

interface FilterOptions {
  selectedDate: string | null;
  searchValue: string;
  selectedApps: string[];
  showFree: boolean;
  showEnrolled: boolean;
}

interface UseFilteredCalendarEventsReturn {
  eventsByMonth: Record<string, LiveTrainingCardProps[]>;
  disabledDateFilter: (date: string) => boolean;
  minDate: Date;
  maxDate: Date;
}

export const useFilteredCalendarEvents = (
  calendarEvents: LiveTrainingCardProps[],
  filters: FilterOptions,
): UseFilteredCalendarEventsReturn => {
  const { selectedDate, searchValue, selectedApps, showFree, showEnrolled } = filters;

  // Filter events by all criteria
  const filteredEvents = useMemo(() => {
    return calendarEvents.filter((event) => {
      // Date filter
      if (selectedDate) {
        const eventDate = new Date(event.startDateTime);
        const eventDateString = eventDate.toISOString().split('T')[0];

        if (eventDateString !== selectedDate) {
          return false;
        }
      }

      // Search filter
      if (searchValue) {
        const searchLower = searchValue.toLowerCase();
        const matchesTitle = event.title.toLowerCase().includes(searchLower);
        const matchesSubtitle = event.subtitle?.toLowerCase().includes(searchLower);

        if (!matchesTitle && !matchesSubtitle) {
          return false;
        }
      }

      // Apps filter
      if (selectedApps.length > 0) {
        const hasMatchingProduct = event.products.some((product) => selectedApps.includes(product));

        if (!hasMatchingProduct) {
          return false;
        }
      }

      // Free/Enrolled filter - only apply if at least one is selected
      // If both are unchecked, show all courses
      if (showFree || showEnrolled) {
        const matchesFreeFilter = showFree && event.isFree;
        const matchesEnrolledFilter = showEnrolled && event.enrolled;

        if (!matchesFreeFilter && !matchesEnrolledFilter) {
          return false;
        }
      }

      return true;
    });
  }, [calendarEvents, selectedDate, searchValue, selectedApps, showFree, showEnrolled]);

  // Group events by month
  const eventsByMonth = useMemo(() => {
    return filteredEvents.reduce(
      (acc, event) => {
        const eventDate = new Date(event.startDateTime);
        const monthKey = eventDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); // "Sep 2025"

        if (!acc[monthKey]) {
          acc[monthKey] = [];
        }

        acc[monthKey].push(event);

        return acc;
      },
      {} as Record<string, LiveTrainingCardProps[]>,
    );
  }, [filteredEvents]);

  // Calculate min and max dates
  const minDate = useMemo(() => {
    const now = new Date();

    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);

  const maxDate = useMemo(() => {
    if (calendarEvents.length === 0) {
      return new Date();
    }

    return new Date(calendarEvents[calendarEvents.length - 1].startDateTime);
  }, [calendarEvents]);

  // Create a set of dates that have events (based on filtered events)
  const datesWithEvents = useMemo(() => {
    const dates = new Set<string>();

    filteredEvents.forEach((event) => {
      const eventDate = new Date(event.startDateTime);
      const dateString = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD

      dates.add(dateString);
    });

    return dates;
  }, [filteredEvents]);

  // Filter function to disable dates without events
  const disabledDateFilter = useCallback(
    (date: string) => {
      // Return true to disable, false to enable
      return !datesWithEvents.has(date);
    },
    [datesWithEvents],
  );

  return {
    eventsByMonth,
    disabledDateFilter,
    minDate,
    maxDate,
  };
};
